import type { AccessLevel } from "@woven/contracts";
import { AlertTriangle, Inbox, ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="status status--loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span className="status-body">{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  tone = "neutral",
  children,
}: {
  title: string;
  body?: string;
  /** "warn" flags something the user is expected to act on (e.g. create it). */
  tone?: "neutral" | "warn";
  children?: ReactNode;
}) {
  const Icon = tone === "warn" ? AlertTriangle : Inbox;
  return (
    <div className={tone === "warn" ? "status status--warn" : "status"} role="status">
      <Icon
        className={tone === "warn" ? "status-mark status-mark--warn" : "status-mark"}
        size={28}
        strokeWidth={1.5}
        absoluteStrokeWidth
        aria-hidden="true"
      />
      <h2 className="status-title">{title}</h2>
      {body ? <p className="status-body">{body}</p> : null}
      {children ? <div className="status-actions">{children}</div> : null}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="status" role="alert">
      <AlertTriangle
        className="status-mark status-mark--danger"
        size={28}
        strokeWidth={1.5}
        absoluteStrokeWidth
        aria-hidden="true"
      />
      <h2 className="status-title">{title}</h2>
      {message ? <p className="status-body">{message}</p> : null}
      {onRetry ? (
        <div className="status-actions">
          <button type="button" className="btn btn--ghost" onClick={onRetry}>
            Try Again
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function PermissionDenied({
  needed,
  current,
}: {
  needed: AccessLevel;
  current: AccessLevel;
}) {
  return (
    <div className="status" role="alert">
      <ShieldAlert
        className="status-mark status-mark--danger"
        size={28}
        strokeWidth={1.5}
        absoluteStrokeWidth
        aria-hidden="true"
      />
      <h2 className="status-title">Permission denied</h2>
      <p className="status-body">
        This area requires <strong>{needed.charAt(0).toUpperCase() + needed.slice(1)}</strong>{" "}
        access; your current access is{" "}
        <strong>{current.charAt(0).toUpperCase() + current.slice(1)}</strong>.
      </p>
    </div>
  );
}
